import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { defaultFolderName, isValidDomain, parseDomains, toggleInSet } from "./onboardShared";
import { useBuyLane, useInventory } from "./useOnboardLanes";

/** Whole Onboard Domains wizard: lane + phase state machine, selection, review config, job polling. */
export function useOnboardWizard() {
  const [preflight, setPreflight] = useState(null);
  const [folders, setFolders] = useState([]);
  const [lane, setLane] = useState("buy");
  const [phase, setPhase] = useState("collect");
  const [err, setErr] = useState("");
  const [ownedText, setOwnedText] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const resetSelection = () => setSelected(new Set());

  const buy = useBuyLane({ setErr, resetSelection });
  const inv = useInventory({ setErr });

  // review config
  const [folderId, setFolderId] = useState("");
  const [newFolderName, setNewFolderName] = useState("");
  const [emailProvider, setEmailProvider] = useState("sparkpost");
  const [siteType, setSiteType] = useState("news");
  const [applyLook, setApplyLook] = useState(true);
  const [emailForward, setEmailForward] = useState("");
  const [spendCap, setSpendCap] = useState("");
  const [spKey, setSpKey] = useState("");
  const [spSub, setSpSub] = useState("");
  const [confirmLarge, setConfirmLarge] = useState(false);
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState(null);
  const poll = useRef(null);

  const buyMode = lane === "buy";
  const importLane = lane === "import" || lane === "import-cf";
  const cfBuy = buyMode && buy.registrar === "cloudflare";
  const pipeline = buyMode ? ["purchase", "site", "cloudflare", "nameservers", "email"] : ["site", "cloudflare", "nameservers", "email"];

  useEffect(() => {
    api.get("/domains/preflight").then((r) => { setPreflight(r.data); setEmailForward(r.data?.default_email_forward || ""); }).catch(() => {});
    api.get("/folders").then((r) => setFolders(r.data?.folders || [])).catch(() => {});
    return () => poll.current && clearInterval(poll.current);
  }, []);

  function switchLane(k) {
    setLane(k); setPhase("collect"); setErr(""); setPlan(null); setJob(null);
    buy.reset();
    if (k === "import" || k === "import-cf") { inv.clear(); inv.load(k); }
  }

  const ownedParsed = useMemo(() => parseDomains(ownedText), [ownedText]);
  const ownedValid = useMemo(() => ownedParsed.filter(isValidDomain), [ownedParsed]);
  const selectedList = useMemo(() => Array.from(selected), [selected]);
  const selectedTotal = useMemo(() => {
    if (!buy.results) return 0;
    const byDomain = Object.fromEntries(buy.results.map((r) => [r.domain, r.total_usd ?? r.price_usd]));
    return selectedList.reduce((s, d) => s + (byDomain[d] || 0), 0);
  }, [selectedList, buy.results]);
  const balance = preflight?.balance?.available ?? null;
  const cfReg = preflight?.cloudflare_registrar || null;

  function toReview() {
    if (lane === "owned") setSelected(new Set(ownedValid));
    if (!folderId) {
      setFolderId("__new__");
      if (!newFolderName) setNewFolderName(defaultFolderName(lane, buy.registrar));
    }
    setPlan(null); setConfirmLarge(false); setPhase("review");
  }

  function body(dry) {
    const base = { domains: selectedList, apply_distinct_look: applyLook, email_forward: emailForward.trim() || null, dry_run: dry, email_provider: emailProvider, site_type: siteType };
    if (folderId === "__new__") base.folder_name = newFolderName.trim() || null;
    else base.folder_id = folderId || null;
    if (emailProvider === "sparkpost" && spKey.trim()) { base.sparkpost_api_key = spKey.trim(); base.sparkpost_subaccount_id = spSub.trim() ? Number(spSub.trim()) : null; }
    if (buyMode) { base.registrar = buy.registrar; base.spend_cap_usd = spendCap.trim() ? Number(spendCap.trim()) : null; base.confirm_large_batch = confirmLarge; }
    if (lane === "import-cf") base.registrar = "cloudflare";
    return base;
  }
  const endpoint = buyMode ? "/domains/onboard" : "/domains/onboard-owned";

  async function submit(dry, fallback) {
    setBusy(true); setErr("");
    try { return (await api.post(endpoint, body(dry))).data; }
    catch (e) { setErr(e?.response?.data?.detail || fallback); return null; }
    finally { setBusy(false); }
  }
  async function doDryRun() { const d = await submit(true, "Preview failed"); if (d) setPlan(d); }

  function confirmMessage() {
    if (!buyMode) return `Onboard ${selectedList.length} owned domain(s)? (No purchase — sets up sites, Cloudflare & email.)`;
    const total = `$${selectedTotal.toFixed(2)}`;
    if (cfBuy) return `Buy ${selectedList.length} domain(s) for ~${total} via Cloudflare Registrar (charged to your Cloudflare account's card, NON-refundable) and onboard them?`;
    return `Buy ${selectedList.length} domain(s) for ~${total} of real Namecheap credit and onboard them? This cannot be undone.`;
  }
  async function doGo() {
    if (!window.confirm(confirmMessage())) return;
    const d = await submit(false, "Onboarding failed");
    if (d) { setPhase("progress"); startPolling(d.job_id); }
  }
  function startPolling(jobId) {
    const tick = async () => {
      try {
        const j = await api.get(`/domains/jobs/${jobId}`);
        setJob(j.data);
        if (j.data.status === "done") { clearInterval(poll.current); poll.current = null; }
      } catch { clearInterval(poll.current); poll.current = null; }
    };
    tick();
    poll.current = setInterval(tick, 3000);
  }

  return {
    preflight, folders, lane, phase, setPhase, err, switchLane, buy, inv,
    buyMode, importLane, cfBuy, pipeline, balance, cfReg,
    ownedText, setOwnedText, ownedParsed, ownedValid,
    selected, selectedList, selectedTotal, toggleDomain: (d) => setSelected((p) => toggleInSet(p, d)), setSelected,
    folderId, setFolderId, newFolderName, setNewFolderName, emailProvider, setEmailProvider, applyLook, setApplyLook, siteType, setSiteType,
    emailForward, setEmailForward, spendCap, setSpendCap, spKey, setSpKey, spSub, setSpSub, confirmLarge, setConfirmLarge,
    plan, busy, job, toReview, doDryRun, doGo,
  };
}
